// Centralized assignment + utilization logic.
// One source of truth for "who is on what, and how loaded are they."
//
// Time-aware: an assignment whose EndDate is already in the past does NOT
// count toward current load — that person has rolled off and is available
// again (bench or their next job).
//
// Load is split into:
//   activeUtil    — sum of allocation % across CURRENT Active (committed) jobs
//   potentialUtil — sum of allocation % across CURRENT Potential (pipeline) jobs
//   totalUtil     — activeUtil + potentialUtil

import { isGeneralSupt, isOpenSeat } from "./roles";

const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
// Allocation defaults to 100% when not recorded — an assigned person is assumed
// full-time on that job unless an explicit percentage is set.
const allocOf = (v) => (v == null || v === "" ? 100 : (Number(v) || 0));
const HIGH_CONFIDENCE = 80; // pipeline above this is treated as "likely"

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// Is this assignment active as of `asOf`? Ended assignments (EndDate < today)
// drop off; not-yet-started assignments still count as committed.
export function assignmentIsCurrent(m, asOf = startOfToday()) {
  if (!m) return false;
  if (m.EndDate) {
    const e = new Date(m.EndDate);
    if (!isNaN(e) && e < asOf) return false;
  }
  return true;
}

// "Bench-eligible" = operational PM / field roles that genuinely sit on a
// bench between jobs. Leadership, precon, accounting, office, and general
// superintendents are never "on the bench."
export function isBenchEligible(person) {
  const r = (person?.CompanyRole || "").toLowerCase();
  if (!r) return false;
  // Explicit non-bench: general superintendent + all office/leadership roles
  if (isGeneralSupt(person?.CompanyRole)) return false;
  if (/(precon|director|\bvp\b|\bevp\b|ceo|cfo|president|estimat|account|admin|office|payroll|controller|\bhr\b|market|expeditor|carpenter|staff|intern|^it$)/.test(r)) return false;
  // Bench-eligible operations roles: PM family, superintendents, PE/FE, QA/QC.
  if (/(sr\.?\s*pm|senior project manager|\bapm\b|assistant project manager|\bpm\b|project manager)/.test(r)) return true;
  if (/(sr\.?\s*supt|senior super|finish supt|asst\.?\s*supt|assistant super|\bsupt\b|superintendent)/.test(r)) return true;
  if (/(project engineer|\bpe\b|field engineer|\bfe\b|qa|qc|quality)/.test(r)) return true;
  return false;
}

// Build a person's assignments, split into CURRENT (live load) and PAST.
// An assignment is past when the member's end date has passed OR the project is
// completed/closed. Only Active & Potential projects create current load.
// A still-Active project whose planned completion date has slipped by is flagged
// `overrun` but stays current (construction jobs routinely run long).
export function personAssignments(person, teamMembers = [], projects = [], asOf = startOfToday()) {
  const mine = (teamMembers || []).filter((m) => m.PersonName === person.FullName);
  const all = mine.map((m) => {
    const project = (projects || []).find((p) => p.Title === m.ProjectJobNumber) || null;
    const status = project?.Status || "Unknown";
    const memberEnded = m.EndDate ? new Date(m.EndDate) < asOf : false;
    const statusDone = /complete|cancel|closed|inactive|archiv/i.test(status);
    const ended = memberEnded || statusDone;
    const planned = project?.CompletionDate ? new Date(project.CompletionDate) : null;
    const overrun = !ended && status === "Active" && planned && planned < asOf;
    return {
      member: m, project,
      jobName: project?.JobName || m.ProjectJobNumber,
      jobNumber: m.ProjectJobNumber,
      role: m.ProjectRole, status,
      confidence: project?.Confidence != null ? num(project.Confidence) : null,
      alloc: allocOf(m.AllocationPercent),
      start: m.StartDate || project?.StartDate || null,
      end: m.EndDate || project?.CompletionDate || null,
      ended, overrun,
    };
  });

  const isLive = (r) => !r.ended && (r.status === "Active" || r.status === "Potential");
  const rows = all.filter(isLive);            // current load
  const pastRows = all.filter((r) => !isLive(r));
  const activeRows = rows.filter((r) => r.status === "Active");
  const potentialRows = rows.filter((r) => r.status === "Potential");

  // Naive sums (kept for back-compat).
  const activeUtil = activeRows.reduce((a, r) => a + r.alloc, 0);
  const potentialUtil = potentialRows.reduce((a, r) => a + r.alloc, 0);
  const totalUtil = activeUtil + potentialUtil;

  // "Now" load = assignments whose window includes today.
  const inWindowNow = (r) => (!r.start || new Date(r.start) <= asOf) && (!r.end || new Date(r.end) >= asOf);
  const nowRows = rows.filter(inWindowNow);
  const activeNowUtil = nowRows.filter((r) => r.status === "Active").reduce((a, r) => a + r.alloc, 0);
  const potentialNowUtil = nowRows.filter((r) => r.status === "Potential").reduce((a, r) => a + r.alloc, 0);
  const currentUtil = activeNowUtil + potentialNowUtil;

  // Peak CONCURRENT load over time — sequential jobs never stack, so someone
  // rolling off one job into another is not "overcommitted."
  const peakUtil = peakConcurrent(rows);

  return {
    rows, pastRows, activeRows, potentialRows, otherRows: [],
    activeUtil, potentialUtil, totalUtil,
    activeNowUtil, potentialNowUtil, currentUtil, peakUtil,
  };
}

// Max simultaneous allocation across a person's assignments (sweep line).
// Assignments without dates are treated as always-on (counted at all times).
function peakConcurrent(rows) {
  let baseline = 0;
  const evts = [];
  rows.forEach((r) => {
    const s = r.start ? new Date(r.start) : null;
    const e = r.end ? new Date(r.end) : null;
    if (!s || !e || isNaN(s) || isNaN(e)) { baseline += r.alloc; return; }
    evts.push({ t: s.getTime(), d: r.alloc });
    evts.push({ t: e.getTime(), d: -r.alloc });
  });
  // At equal timestamps, process ends (negative) before starts so touching
  // intervals don't count as overlapping.
  evts.sort((a, b) => a.t - b.t || a.d - b.d);
  let run = 0, peak = baseline;
  evts.forEach((ev) => { run += ev.d; if (baseline + run > peak) peak = baseline + run; });
  return Math.round(peak);
}

// Classify a person's overall state.
//   green  → on an active/committed job
//   yellow → pipeline-only, at least one high-confidence (>80%) job
//   gold   → pipeline-only, lower confidence
//   red    → bench-eligible but unassigned (truly on the bench)
//   muted  → not bench-eligible and unassigned (support / leadership — not "benched")
export function assignmentState(u, benchEligible = true) {
  // Presence of an active assignment makes someone "on an active job" even if
  // no allocation % was recorded for it.
  if (u.activeRows.length) return { key: "active", label: "On active job", color: "var(--green)", tone: "green" };
  if (u.potentialRows.length) {
    const highConf = u.potentialRows.some((r) => (r.confidence || 0) > HIGH_CONFIDENCE);
    return highConf
      ? { key: "likely", label: "Likely (pipeline >80%)", color: "var(--gold)", tone: "yellow" }
      : { key: "potential", label: "Pipeline only", color: "var(--gold-text)", tone: "gold" };
  }
  if (!benchEligible) return { key: "support", label: "Support / leadership", color: "var(--text-muted)", tone: "muted" };
  return { key: "bench", label: "On bench", color: "var(--red)", tone: "red" };
}

// When does this person run out of work?
//
// A member row with no EndDate inherits the PROJECT's completion date — that's
// the convention everywhere else (the Gantt bars, personAssignments above, the
// move wizard). Reading a blank end date as "employed indefinitely" is how
// someone whose job wraps up next week vanishes from the 90-day forecast.
//
// A finished job isn't a home either, so assignments on completed/cancelled
// projects are ignored, same rule personAssignments uses.
//
// Returns { covered, latest, indefinite }:
//   covered     — has work reaching the horizon
//   latest      — the last day they're spoken for (null when indefinite)
//   indefinite  — neither the member row nor the job has an end date at all
export function homeThrough(fullName, teamMembers = [], projects = [], horizon) {
  const projectFor = (m) => (projects || []).find((p) => p.Title === m.ProjectJobNumber) || null;
  const live = (teamMembers || []).filter((m) => {
    if (m.PersonName !== fullName || isOpenSeat(m)) return false;
    return !/complete|cancel|closed|inactive|archiv/i.test(projectFor(m)?.Status || "");
  });
  if (!live.length) return { covered: false, latest: null, indefinite: false };

  let latest = null;
  for (const m of live) {
    const raw = m.EndDate || projectFor(m)?.CompletionDate || null;
    if (!raw) return { covered: true, latest: null, indefinite: true };
    const e = new Date(raw);
    if (isNaN(e)) return { covered: true, latest: null, indefinite: true };
    if (!latest || e > latest) latest = e;
  }
  return { covered: latest >= horizon, latest, indefinite: false };
}

// Overcommitted only when concurrent (overlapping) load exceeds 100%.
export const isOvercommitted = (u) => u.peakUtil > 100;

// Build the full roster view for the Assignments page in one pass.
export function buildAssignmentRoster(people = [], teamMembers = [], projects = []) {
  const asOf = startOfToday();
  return (people || [])
    .filter((p) => p.IsActive !== false)
    .map((person) => {
      const util = personAssignments(person, teamMembers, projects, asOf);
      const benchEligible = isBenchEligible(person);
      const state = assignmentState(util, benchEligible);
      return {
        person, util, state, benchEligible,
        overcommitted: isOvercommitted(util),
        bench: benchEligible && util.rows.length === 0,
      };
    })
    .sort((a, b) => b.util.totalUtil - a.util.totalUtil);
}

export { HIGH_CONFIDENCE };
